import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { zodResolver } from '@hookform/resolvers/zod';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import { AxiosError } from 'axios';
import { z } from 'zod';

import Button from '../../common/Button';
import { toastPromise } from '../../common/utils';
import {
  useGetModelOutput,
  useUpdateModel,
} from '../../hooks/tanstack/useModelsHook';
import {
  useGetDistinguishModel,
  useGetRouteParams,
} from '../../hooks/tanstack/useUtilsHook';
import FormInput from '../form/FormInput';

const SeatLockPeriodConfigurationSchema = z.object({
  label: z.string(),
  value: z.string(),
});

type SeatLockPeriodConfigurationSchemaType = z.infer<
  typeof SeatLockPeriodConfigurationSchema
>;

const SeatLockPeriodConfiguration = () => {
  const { modelId, orgId, projectId } = useGetRouteParams();

  const [openSelectUpdateModelDialog, setOpenUpdateModelDialog] =
    useState(false);

  const { model, isDefault, isRunningModelOutputServer } =
    useGetDistinguishModel();

  const { refetch: refetchModelOutput } = useGetModelOutput(
    {
      orgId,
      projectId,
      modelId,
    },
    {
      enabled: !!orgId && !!projectId && !!modelId,
    }
  );

  const { mutateAsync: updateModel, isPending: isUpdatingModel } =
    useUpdateModel({
      onSuccess: () => {
        refetchModelOutput();
        handleCloseUpdateModelDialog();
      },
      onError: (error) => {
        if (error instanceof AxiosError && error.response) {
          setError('value', {
            message: error.response['data']['seat_lock_period'][0],
          });
        }
      },
    });

  const form = useForm<SeatLockPeriodConfigurationSchemaType>({
    resolver: zodResolver(SeatLockPeriodConfigurationSchema),
    defaultValues: {
      value: '',
      label: '',
    },
  });

  const { handleSubmit, setValue, watch, reset, setError } = form;

  useEffect(() => {
    if (model) {
      const value = model.seat_lock_period.toString();

      reset({
        label: value,
        value,
      });
    }
  }, [model]);

  const handleCloseUpdateModelDialog = () => {
    setOpenUpdateModelDialog(false);
  };

  const handleOpenUpdateModelDialog = () => {
    setOpenUpdateModelDialog(true);
  };

  const handleOnSubmit = () => {
    const { value } = watch();

    if (!model) {
      return;
    }

    if (!value) {
      setError('value', {
        message: 'This field is required',
      });
      return;
    } else if (Number(value) < 0) {
      setError('value', {
        message: 'Value should be greater than 0',
      });
      return;
    }

    const res = updateModel({
      orgId,
      projectId,
      modelId,
      data: {
        seat_lock_period: Number(value),
        dataset: model.dataset.id,
      },
    });

    res.then(() => {
      setValue('label', value);
    });

    toastPromise({
      promise: res,
      content: 'Seat lock period updated successfully',
    });
  };

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit(handleOnSubmit)}>
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <h1 className="text-xl font-bold">Seat Lock Period (months)</h1>
            {!model?.is_base && !model?.is_locked && isDefault && (
              <Button
                variant="outlined_red"
                disabled={isRunningModelOutputServer}
                onClick={handleOpenUpdateModelDialog}
              >
                Edit
              </Button>
            )}
          </div>
          <div className="w-[30%]">
            <FormInput disabled name={'label'} />
          </div>
        </div>
        <Dialog
          maxWidth="sm"
          fullWidth
          open={openSelectUpdateModelDialog}
          onClose={handleCloseUpdateModelDialog}
        >
          <DialogTitle style={{ cursor: 'move' }}>
            <h2 className="font-bold">Edit Seat Lock Period (months)</h2>
          </DialogTitle>
          <DialogContent>
            <div className="w-full">
              <FormInput
                placeholder="Enter retirement age"
                type="number"
                name="value"
              />
            </div>
          </DialogContent>
          <DialogActions>
            <div className="flex gap-3 px-4 pb-3">
              <Button
                autoFocus
                variant="outlined"
                type="button"
                onClick={handleCloseUpdateModelDialog}
                tabIndex={3}
              >
                Cancel
              </Button>
              <Button
                autoFocus
                variant="contained_blue"
                disabled={isUpdatingModel}
                loading={isUpdatingModel}
                onClick={handleOnSubmit}
                tabIndex={3}
              >
                Save
              </Button>
            </div>
          </DialogActions>
        </Dialog>
      </form>
    </FormProvider>
  );
};

export default SeatLockPeriodConfiguration;
